import { CalendarOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import { message } from '@/components/Message';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useUpdate } from 'react-use';
import { useImmer } from 'use-immer';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { POSBaseInfoContext } from '@/components/LayoutPOS/Synchronizer';
import PosButton from '@/components/PosButton';
import { addGlobalEventListener, removeGlobalScanListener } from '@/components/QRScanner/GlobalScanner';
import { PreOrder } from '@/models/order';
import { ReceivingMode } from '@/models/payment';
import { POSPaymentResponse } from '@/models/pos';
import { getIsPaidOrder } from '@/models/preOrder';
import { UserFullInfo, UserFullInfoContext } from '@/models/user';
import { noop } from '@/utils/common';
import { playSound } from '@/utils/sound';
import { AppointmentTimelinePopup } from '@/components/AppointmentTimeline/Timeline';
import CheckoutArea from './CheckoutArea';
import CommandHistory from './components/CommandHistory';
import ConsumerDisplay from './components/ConsumerDisplay';
import BindUser from './FastActions/BindUser';
import ScanResponder from './ScanResponder';
import Settlement from './Settlement';
import PaymentSuccess from './Settlement/PaymentSuccess';
import SkuArea from './SkuArea';
import { Station, StationRef } from './Station';
import styles from './style.less';
import { APIError } from 'api/types';

interface Props {
  previewMode?: boolean;
  previews?: ('skuArea' | 'checkoutArea')[];
}

const EmptyPreOrder = {
  specifications: [],
  userFullInfo: null,
  cards: [],
  addTime: undefined,
} as PreOrder;

const POS: React.FC<Props> = ({ previewMode = false, previews = [] }: Props) => {
  const forceUpdate = useUpdate();
  // 服务单记录
  const stationRef = useRef<StationRef>();
  const { categoryList, pointRules, cardList } = useContext(POSBaseInfoContext);
  const [, , helpers] = useContext(BaseInfoContext);
  // 用户信息
  // const [userFullInfo, setUserFullInfo] = useImmer<UserFullInfo>(null);
  // 待结清单
  // const [addedSpecifications, setAddedSpecifications] = useImmer<SKUSpecification[]>([]);
  const [preOrder, setPreOrder] = useImmer<PreOrder>(EmptyPreOrder);

  // 订单完成信息
  const [orderCompleteInfo, setOrderCompleteInfo] = useImmer<POSPaymentResponse>(null);

  // 购卡
  // const [addedCards, setAddedCards] = useImmer<Card[]>([]);

  // 结算弹窗模式
  const [receivingMode, setReceivingMode] = useState(ReceivingMode.INVALID);

  // 标签打印
  // const printLabel = usePrintOrderLabel(true);
  // const printOrder = usePrintOrder();
  // const [scannedGoodsSn, setScannedGoodsSn] = useState<string>();

  useEffect(() => {
    const listenerKeys = [
      // 绑定用户
      addGlobalEventListener('actionUserBind', ({ payload }) => {
        // setUserFullInfo(() => payload as UserFullInfo);
        setPreOrder(draft => {
          draft.userFullInfo = payload as UserFullInfo;
        });
      }),
      // 用户信息变更, 例发放了优惠券等
      addGlobalEventListener('actionUserUpdate', ({ payload }) => {
        setPreOrder(draft => {
          // 如果当前已经关联了客户，则更新客户信息
          if (draft.userFullInfo) {
            draft.userFullInfo = payload as UserFullInfo;
          }
        });
      }),
      // 解绑用户
      addGlobalEventListener('actionUserUnBind', ({}) => {
        // setUserFullInfo(() => null);
        setPreOrder(draft => {
          draft.userFullInfo = null;
        });
      }),
      // 用户充值
      addGlobalEventListener('actionUserRecharge', ({ payload }) => {
        // setUserFullInfo(() => payload as UserFullInfo);
        setPreOrder(draft => {
          draft.userFullInfo = payload as UserFullInfo;
        });
        setReceivingMode(ReceivingMode.RECHARGE);
      }),
      // 线上订单-开始服务
      addGlobalEventListener('actionStartOrder', ({ payload }) => {
        // stationRef.current.push({
        //   specifications: (payload as PreOrder).specifications,
        //   userFullInfo: (payload as PreOrder).userFullInfo,
        //   operator: (payload as PreOrder).operator,
        //   orderSn: (payload as PreOrder).orderSn,
        //   // addTime: dayjs().format(DateTimeFormat),
        // });
        stationRef.current.push(payload as PreOrder);
        // 清空
        setPreOrder(() => EmptyPreOrder);
      }),
      // 线上订单-结束服务
      addGlobalEventListener('actionCompleteOrder', ({ payload }) => {
        const orderCompleteInfo = payload as POSPaymentResponse;
        setOrderCompleteInfo(() => {
          orderCompleteInfo.type = 'service';
          return orderCompleteInfo;
        });
        stationRef.current.popByOrderSn(orderCompleteInfo.order.orderSn);
      }),
    ];
    return () => {
      removeGlobalScanListener(...listenerKeys);
    };
  }, []);

  const onCheckout = () => {
    if (!preOrder.specifications.length) {
      message.error('请添加要结账的商品');
      playSound('error');
      return;
    }
    playSound('click_light');
    setReceivingMode(ReceivingMode.CART);
  };

  // 结账快捷键
  useHotkeys(
    'Enter',
    e => {
      const tagName = (e.target as HTMLElement).tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea') {
        return;
      }

      const hasOpenedModals =
        Array.from(document.querySelectorAll('.ant-modal-wrap')).filter(
          s => window.getComputedStyle(s).getPropertyValue('display') !== 'none'
        ).length > 0;

      if (!hasOpenedModals) {
        onCheckout();
      }
    },
    [onCheckout]
  );

  return (
    // </GlobalScanner>
    <UserFullInfoContext.Provider value={{ userFullInfo: preOrder.userFullInfo, updateUserFullInfo: noop }}>
      {!previewMode ? (
        <>
          <ScanResponder />
          {orderCompleteInfo ? (
            <PaymentSuccess
              onClose={() => {
                setOrderCompleteInfo(() => null);
              }}
              order={orderCompleteInfo.order}
              user={orderCompleteInfo.user}
              skuList={orderCompleteInfo.skuList}
              transactionList={orderCompleteInfo.transactionList}
              cardUserList={orderCompleteInfo.cardUserList}
              type={orderCompleteInfo.type}
              onDataChange={(user, order) => {
                setOrderCompleteInfo(draft => {
                  draft.user = user || draft.user;
                  draft.order = order || draft.order;
                });
              }}
            />
          ) : null}

          {receivingMode !== ReceivingMode.INVALID && (
            <Settlement
              pointRules={pointRules}
              onReceived={orderCompleteInfoNew => {
                setPreOrder(draft => {
                  if (receivingMode === ReceivingMode.RECHARGE) {
                    // 充值，更新用户信息(余额)
                    draft.userFullInfo.user = orderCompleteInfoNew.user;
                  } else if (receivingMode === ReceivingMode.CART) {
                    // 商品、服务结算
                    // 重置: 清空商品 / 取消关联客户
                    return EmptyPreOrder;
                  }
                });
                setOrderCompleteInfo({ type: 'pay', ...orderCompleteInfoNew });
              }}
              preOrder={preOrder}
              onClose={() => {
                if (receivingMode === ReceivingMode.CARD) {
                  // 结算窗口关闭时，清空要购买的预付卡 (取消购买)
                  setPreOrder(draft => {
                    draft.cards = [];
                  });
                }
                setReceivingMode(ReceivingMode.INVALID);
              }}
              mode={receivingMode}
            />
          )}
        </>
      ) : null}
      <div className={styles.main}>
        {!previewMode || previews.includes('skuArea') ? (
          <SkuArea
            // scannedGoodsSn={scannedGoodsSn}
            previewMode={previewMode}
            cardList={cardList}
            categoryList={categoryList}
            actions={
              <>
                <BindUser userFullInfo={preOrder.userFullInfo} />
                <PosButton
                  type={'primary'}
                  icon={<MoneyCollectOutlined />}
                  onClick={() => {
                    setReceivingMode(ReceivingMode.DIRECT);
                  }}
                />
                <CommandHistory />
                <ConsumerDisplay />
                <AppointmentTimelinePopup>
                  <PosButton icon={<CalendarOutlined />} type="primary" onClick={() => {}} />
                </AppointmentTimelinePopup>
              </>
            }
            onAddCard={card => {
              playSound('click');
              setPreOrder(draft => {
                // 一次只能购买一张卡
                draft.cards = [card];
              });
              setReceivingMode(ReceivingMode.CARD);
            }}
            onAdd={specification => {
              setPreOrder(draft => {
                const findIndex = draft.specifications.findIndex(
                  existsSpecification => existsSpecification.id === specification.id
                );

                if (
                  // 库存不足
                  // 要添加的数量大于库存量
                  // 或
                  // 要添加的数量+已添加的数量大于库存量
                  specification.stock < specification.number ||
                  (findIndex > -1 &&
                    specification.stock < draft.specifications[findIndex].number + specification.number)
                ) {
                  message.destroy();
                  message.error('该商品剩余库存不足');
                  playSound('error');
                  return;
                }

                if (findIndex === -1) {
                  draft.specifications.unshift(specification);
                } else {
                  draft.specifications[findIndex].number += specification.number;
                }
                playSound('click');
              });
            }}
          />
        ) : null}
        {!previewMode || previews.includes('checkoutArea') ? (
          <div className={styles.mainRight}>
            <CheckoutArea
              preOrder={preOrder}
              onRequestChangeNumber={(item, num) => {
                setPreOrder(draft => {
                  const findIndex = draft.specifications.findIndex(i => i.id === item.id);
                  draft.specifications[findIndex].number = num;
                });
              }}
              onRequestDelete={item => {
                setPreOrder(draft => {
                  const findIndex = draft.specifications.findIndex(i => i.id === item.id);
                  draft.specifications.splice(findIndex, 1);
                });
              }}
              onRequestClear={() => {
                if (preOrder.specifications && preOrder.specifications.length) {
                  setPreOrder(draft => {
                    draft.specifications.splice(0, draft.specifications.length);
                  });
                }
              }}
              onRequestDeletePreOrder={async preOrder => {
                // 不等待结果
                await stationRef.current?.deletePreOrder(preOrder);

                // 清空
                if (preOrder.specifications && preOrder.specifications.length) {
                  setPreOrder(draft => {
                    draft.specifications.splice(0, draft.specifications.length);
                  });
                }
              }}
              onRequestPushToStation={async () => {
                if (preOrder.specifications && preOrder.specifications.length) {
                  try {
                    playSound('click_light');
                    await stationRef.current.push(preOrder);

                    // 清空货品 / 取消关联用户
                    setPreOrder(() => EmptyPreOrder);
                    message.success('操作成功');
                  } catch (e) {
                    const err = e as APIError;
                    message.error('出错了:' + err.message);
                  }
                } else {
                  message.error('请先添加商品或服务');
                  playSound('error');
                }
              }}
              onCheckout={() => {
                // onOrder(order);
                onCheckout();
              }}
            />
            <Station
              onStationChange={forceUpdate}
              ref={stationRef}
              onPop={preOrder => {
                if (getIsPaidOrder(preOrder)) {
                  // 已入库的订单, 直接显示订单详情
                  helpers.showOrderModal({
                    orderSn: preOrder.orderSn,
                  });
                } else {
                  setPreOrder(() => preOrder);
                }
              }}
              showOverrideWarning={Boolean(preOrder.specifications && preOrder.specifications.length)}
              onRequestDelete={stationRef.current?.deletePreOrder}
            />
          </div>
        ) : null}
      </div>
    </UserFullInfoContext.Provider>
  );
};

export default POS;
